import { useMemo, useState } from 'react';
import { finite, number, date } from '../../utils/format.js';

export default function EquityCurveChart({ equity, label = 'Strategy', benchmarkLabel = 'Benchmark' }) {
  const [hover, setHover] = useState(null);
  const plot = useMemo(() => {
    const rows = Array.isArray(equity) ? equity : [];
    const values = rows.flatMap((r) => [r.equity, r.benchmark]).filter(finite).map(Number);
    if (!values.length) return null;
    let min = Infinity, max = -Infinity;
    for (const value of values) { min = Math.min(min, value); max = Math.max(max, value); }
    if (min === max) { min -= Math.max(1, Math.abs(min) * .01); max += Math.max(1, Math.abs(max) * .01); }
    const x = (i) => 82 + i / Math.max(1, rows.length - 1) * 680;
    const y = (v) => 218 - (Number(v) - min) / (max - min) * 194;
    // Keep bucket extrema, including narrow drawdowns, in a bounded-size path.
    const path = (key) => {
      const step = Math.max(1, Math.ceil(rows.length / 700));
      const selected = new Set([0, rows.length - 1]);
      for (let start = 0; start < rows.length; start += step) {
        let lo = start, hi = start;
        for (let i = start; i < Math.min(rows.length, start + step); i++) {
          if (!finite(rows[i][key])) { selected.add(i); continue; }
          if (!finite(rows[lo][key]) || rows[i][key] < rows[lo][key]) lo = i;
          if (!finite(rows[hi][key]) || rows[i][key] > rows[hi][key]) hi = i;
        }
        selected.add(lo); selected.add(hi);
      }
      let started = false;
      return [...selected].sort((a, b) => a - b).map((i) => {
        if (!finite(rows[i][key])) { started = false; return ''; }
        const command = started ? 'L' : 'M'; started = true;
        return `${command}${x(i).toFixed(2)},${y(rows[i][key]).toFixed(2)}`;
      }).join(' ');
    };
    return { rows, min, max, x, y, strategy: path('equity'), benchmark: path('benchmark'),
      hasBenchmark: rows.some((r) => finite(r.benchmark)) };
  }, [equity]);
  if (!plot) return <div className="aq-bt-empty">No completed equity curve available.</div>;
  const selected = hover === null ? null : plot.rows[Math.min(hover, plot.rows.length - 1)];
  return <figure style={{ margin: 0, width: '100%' }}>
    <svg viewBox="0 0 800 255" role="img" aria-label={`${label} net asset value from ${date(plot.rows[0].date)} to ${date(plot.rows.at(-1).date)}`}
      style={{ width: '100%', minHeight: 180 }} onPointerLeave={() => setHover(null)}
      onPointerMove={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        const x = (event.clientX - box.left) / box.width * 800;
        setHover(Math.max(0, Math.min(plot.rows.length - 1, Math.round((x - 82) / 680 * (plot.rows.length - 1)))));
      }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const value = plot.min + (plot.max - plot.min) * i / 4;
        return <g key={i}><line x1="82" x2="762" y1={plot.y(value)} y2={plot.y(value)} stroke="currentColor" opacity=".15" />
          <text x="76" y={plot.y(value) + 4} textAnchor="end" fontSize="11" fill="currentColor">{number(value, 0)}</text></g>;
      })}
      {plot.min < 0 && plot.max > 0 && <line x1="82" x2="762" y1={plot.y(0)} y2={plot.y(0)} stroke="#ff5555" strokeDasharray="4" />}
      {plot.hasBenchmark && <path d={plot.benchmark} fill="none" stroke="#93a0ba" strokeWidth="1.5" />}
      <path d={plot.strategy} fill="none" stroke="#398bff" strokeWidth="2" />
      <text x="82" y="246" fontSize="11" fill="currentColor">{date(plot.rows[0].date)}</text>
      <text x="762" y="246" textAnchor="end" fontSize="11" fill="currentColor">{date(plot.rows.at(-1).date)}</text>
      {selected && <line x1={plot.x(Math.min(hover, plot.rows.length - 1))} x2={plot.x(Math.min(hover, plot.rows.length - 1))} y1="24" y2="218" stroke="currentColor" opacity=".4" />}
    </svg>
    <figcaption>{selected ? `${date(selected.date)} · ${label}: ${number(selected.equity)}` : `${label} · net asset value`}
      {plot.hasBenchmark && ` · ${benchmarkLabel}${selected ? `: ${number(selected.benchmark)}` : ''}`}</figcaption>
  </figure>;
}
